import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface ReportsTileProps {
  icon: LucideIcon;
  title: string;
  description: string;
  href?: string;
  onClick?: () => void;
  comingSoon?: boolean;
  badge?: string;
  className?: string;
}

export function ReportsTile({
  icon: Icon,
  title,
  description,
  href,
  onClick,
  comingSoon = false,
  badge,
  className,
}: ReportsTileProps) {
  const isClickable = !comingSoon && (href || onClick);

  const handleClick = () => {
    if (comingSoon) return;
    if (onClick) {
      onClick();
    } else if (href) {
      window.location.href = href;
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative flex flex-col gap-3 rounded-lg border bg-card p-5 transition-all duration-150",
        isClickable
          ? "cursor-pointer hover:border-primary/50 hover:shadow-sm hover:bg-accent/30"
          : "opacity-60 cursor-not-allowed",
        className
      )}
    >
      {/* Icon + Badge row */}
      <div className="flex items-start justify-between gap-2">
        <div className={cn(
          "flex h-10 w-10 items-center justify-center rounded-md",
          isClickable ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
        )}>
          <Icon className="h-5 w-5" />
        </div>
        {comingSoon && (
          <Badge variant="secondary" className="text-xs font-normal">
            Coming soon
          </Badge>
        )}
        {badge && !comingSoon && (
          <Badge variant="outline" className="text-xs font-normal">
            {badge}
          </Badge>
        )}
      </div>

      {/* Text */}
      <div className="space-y-1">
        <p className={cn(
          "text-sm font-medium leading-tight",
          !isClickable && "text-muted-foreground"
        )}>
          {title}
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {description}
        </p>
      </div>

      {/* Hover arrow — only for clickable tiles */}
      {isClickable && (
        <span className="absolute bottom-4 right-4 text-muted-foreground/40 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
          →
        </span>
      )}
    </div>
  );
}
